import React, { useState, useEffect } from "react";
import MunicipalitySearch from "./MunicipalitySearch";
import { Municipality } from "../types";
import {
  loadBudgetData,
  ParsedBudgetData,
} from "../services/budgetDataService";
import {
  fetchNationalStats,
  IssueAggregation,
} from "../services/citizenAgendaService";
import { getIssueIcon } from "./InfoSidebar";
import {
  Dices,
  BarChart3,
  Coins,
  TrendingDown,
  Building2,
  Wallet,
  Calculator,
} from "lucide-react";

interface NationalDashboardProps {
  onMunicipalitySelect: (municipality: Municipality) => void;
  onRandomSelect: () => void;
}

interface NationalStatsData {
  totalObt: number;
  totalBudget: number;
  avgBudget: number;
}

const NationalDashboard: React.FC<NationalDashboardProps> = ({
  onMunicipalitySelect,
  onRandomSelect,
}) => {
  const [richest, setRichest] = useState<ParsedBudgetData[]>([]);
  const [poorest, setPoorest] = useState<ParsedBudgetData[]>([]);
  const [nationalStats, setNationalStats] = useState<{
    total_votes: number;
    top_issues: IssueAggregation[];
  } | null>(null);
  const [quickStats, setQuickStats] = useState<NationalStatsData | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load budget data for 2567
        const budgetData = await loadBudgetData(2567);

        // Calculate quick stats
        const totalObt = budgetData.length;
        const totalBudget = budgetData.reduce(
          (sum, m) => sum + m.totalRevenue,
          0
        );
        const avgBudget = totalBudget / totalObt;
        setQuickStats({ totalObt, totalBudget, avgBudget });

        // Sort by total revenue
        const sorted = [...budgetData].sort(
          (a, b) => b.totalRevenue - a.totalRevenue
        );
        setRichest(sorted.slice(0, 5));
        setPoorest(sorted.slice(-5).reverse());

        // Fetch national stats
        const stats = await fetchNationalStats();
        setNationalStats(stats);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatMoney = (amount: number) => {
    return (amount * 1000000).toLocaleString("th-TH", {
      maximumFractionDigits: 0,
    });
  };

  const formatShortMoney = (amount: number) => {
    // amount is in millions (ล้าน)
    if (amount >= 100000) {
      // 100,000+ ล้าน = แสนล้าน
      return `${(amount / 100000).toFixed(1)} แสนล้าน`;
    }
    if (amount >= 10000) {
      // 10,000+ ล้าน = หมื่นล้าน
      return `${(amount / 10000).toFixed(1)} หมื่นล้าน`;
    }
    if (amount >= 1000) {
      // 1,000+ ล้าน = พันล้าน
      return `${(amount / 1000).toFixed(1)} พันล้าน`;
    }
    return `${amount.toFixed(0)} ล้าน`;
  };

  const getTagName = (tagId: string): string => {
    const names: { [key: string]: string } = {
      road: "ถนน",
      water: "น้ำประปา",
      electricity: "ไฟฟ้า",
      garbage: "ขยะ",
      flood: "น้ำท่วม",
      education: "การศึกษา",
      health: "สุขภาพ",
      safety: "ความปลอดภัย",
      park: "สวนสาธารณะ",
      other: "อื่นๆ",
    };
    return names[tagId] || tagId;
  };

  return (
    <div className="national-dashboard">
      {/* Hero Section */}
      <div className="dashboard-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            คุณรู้ไหมว่า อบต. แถวบ้านคุณ <br />
            <span className="highlight">มีงบกี่ล้าน?</span>
          </h1>
          <p className="hero-subtitle">
            ตรวจสอบงบประมาณและร่วมโหวตวาระประชาชนในพื้นที่ของคุณ
          </p>

          <div className="hero-search-box">
            <MunicipalitySearch onMunicipalitySelect={onMunicipalitySelect} />
          </div>

          <div className="hero-actions">
            <button className="random-btn" onClick={onRandomSelect}>
              <Dices size={20} /> สุ่มดูตำบลอื่น
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-content">
        {/* Quick Stats Banner */}
        {quickStats && (
          <div className="quick-stats-banner">
            <div className="quick-stat-item">
              <div className="quick-stat-icon">
                <Building2 size={24} />
              </div>
              <div className="quick-stat-info">
                <div className="quick-stat-value">
                  {quickStats.totalObt.toLocaleString()}
                </div>
                <div className="quick-stat-label">อบต. ทั่วประเทศ</div>
              </div>
            </div>
            <div className="quick-stat-item">
              <div className="quick-stat-icon">
                <Wallet size={24} />
              </div>
              <div className="quick-stat-info">
                <div className="quick-stat-value">
                  {formatShortMoney(quickStats.totalBudget)}
                </div>
                <div className="quick-stat-label">งบประมาณรวม</div>
              </div>
            </div>
            <div className="quick-stat-item">
              <div className="quick-stat-icon">
                <Calculator size={24} />
              </div>
              <div className="quick-stat-info">
                <div className="quick-stat-value">
                  {quickStats.avgBudget.toFixed(1)} ล้าน
                </div>
                <div className="quick-stat-label">เฉลี่ยต่อ อบต.</div>
              </div>
            </div>
          </div>
        )}

        {/* National Agenda */}
        <div className="dashboard-section">
          <h2>
            <BarChart3
              size={28}
              style={{ verticalAlign: "middle", marginRight: "8px" }}
            />{" "}
            วาระประชาชนระดับประเทศ
          </h2>
          <p className="section-desc">
            สิ่งที่คนไทยอยากให้แก้ไขมากที่สุด (จาก{" "}
            {nationalStats?.total_votes || 0} โหวต)
          </p>

          <div className="national-agenda-list">
            {nationalStats?.top_issues.map((issue, index) => (
              <div key={issue.tag_id} className="agenda-item">
                <div className="agenda-rank">{index + 1}</div>
                <div className="agenda-icon">{getIssueIcon(issue.tag_id)}</div>
                <div className="agenda-info">
                  <div className="agenda-label">
                    เรื่อง{getTagName(issue.tag_id)}
                  </div>
                  <div className="agenda-bar-container">
                    <div
                      className="agenda-bar"
                      style={{
                        width: `${
                          (issue.vote_count /
                            (nationalStats.total_votes || 1)) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
                <div className="agenda-count">{issue.vote_count} โหวต</div>
              </div>
            ))}
          </div>
        </div>

        {/* Richest / Poorest */}
        <div className="dashboard-grid">
          <div className="dashboard-card richest">
            <h3>
              <Coins size={22} style={{ color: "var(--primary)" }} /> 5 อบต.
              ที่รวยที่สุด
            </h3>
            <p className="card-year-note">ข้อมูลงบประมาณปี พ.ศ. 2567</p>
            <div className="ranking-list">
              {richest.map((m, i) => (
                <div key={i} className="ranking-item">
                  <div className="rank">{i + 1}</div>
                  <div className="name">
                    <div>{m.name}</div>
                    <div className="province">{m.province}</div>
                  </div>
                  <div className="amount">
                    {formatMoney(m.totalRevenue)} บาท
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-card poorest">
            <h3>
              <TrendingDown size={22} style={{ color: "var(--secondary)" }} /> 5
              อบต. ที่งบน้อยที่สุด
            </h3>
            <p className="card-year-note">ข้อมูลงบประมาณปี พ.ศ. 2567</p>
            <div className="ranking-list">
              {poorest.map((m, i) => (
                <div key={i} className="ranking-item">
                  <div className="rank">{i + 1}</div>
                  <div className="name">
                    <div>{m.name}</div>
                    <div className="province">{m.province}</div>
                  </div>
                  <div className="amount">
                    {formatMoney(m.totalRevenue)} บาท
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NationalDashboard;
